import React, { useEffect, useRef, useState } from "react";
import { motion, useInView, animate } from "framer-motion";
import { MapPinIcon } from "lucide-react";
import { Reveal } from "../ui/Reveal";
import { SectionTitle } from "../ui/SectionTitle";
import { TripleFilterInteractive } from "./TripleFilterInteractive";
import { desserts } from "../../data/images";
const wants = [{
  text: "Ingredientes que se consiguen en el supermercado de la esquina (no harinas especiales de $25)",
  image: desserts.carrotCake
}, {
  text: "Recetas que funcionen incluso si \"se te quema el agua\"",
  image: desserts.blueberryCheesecakes
}, {
  text: "Postres que sus hijos coman sin saber que son saludables",
  image: desserts.chocolateDonut
}, {
  text: "Un sistema que se adapte a su ritmo, no a un plan de comidas imposible",
  image: desserts.raspberryCrepes
}];
export function RevelationSection() {
  return <section className="bg-cream py-20 lg:py-28" data-id="element-381" data-mp="1f8dj2tb46xeu">
      <div className="mx-auto max-w-6xl px-5" data-id="element-382" data-mp="1fa8385ekalhv">
        <SectionTitle intro="Durante años, trabajé como nutricionista y vi a miles de mujeres como tú luchando con esto. Y descubrí algo que cambió todo:" data-id="element-383" data-mp="27hsysf8uy053">
          Lo Que Nadie Te Cuenta Sobre los Postres "Realmente" Saludables
        </SectionTitle>

        <Reveal className="mx-auto mt-10 max-w-3xl text-center" data-id="element-384" data-mp="2h9f9maha9x7b">
          <p className="font-display text-2xl font-bold leading-snug text-cocoa sm:text-3xl" data-id="element-385" data-mp="2cgvrd07nidw6">
            El problema nunca fue el postre. El problema fue que nadie te enseñó la <span className="text-berry" data-id="element-386" data-mp="2l9hbajn0qqml">QUÍMICA</span> correcta.
          </p>
        </Reveal>

        <Reveal className="mt-14" data-id="element-387" data-mp="2lbts2th1iuy2">
          <TripleFilterInteractive data-id="element-388" data-mp="306zxbe8bapqc" />
        </Reveal>

        <div className="mx-auto mt-14 max-w-3xl" data-id="element-389" data-mp="304dq5fpc2c8n">
          <Reveal delay={0.05} className="space-y-5 text-center text-lg leading-relaxed text-cocoa-light" data-id="element-390" data-mp="2v8omb06a696j">
            <p data-id="element-391" data-mp="15ti4pct1v2og">
              Cualquiera puede quitar azúcar de una receta. Pero quitar <b className="text-cocoa" data-id="element-392" data-mp="10u5mx6h1clsq">azúcar, gluten Y lactosa</b> mientras
              mantienes la humedad, la textura esponjosa, el sabor adictivo — eso es ciencia.
            </p>
            <p data-id="element-393" data-mp="1uj386n8u7oy1">Es lo que distingue un postre que sabe a castigo de uno que sabe a celebración.</p>
            <p data-id="element-394" data-mp="1zipl0vkr736b">
              Y aquí está lo interesante: cuando la química es correcta, tu cuerpo realmente lo siente.{" "}
              <b className="text-cocoa" data-id="element-395" data-mp="24ikd32g9lysy">No hay hinchazón. No hay culpa.</b> Solo satisfacción genuina. De verdad.
            </p>
          </Reveal>
        </div>

        <div className="mt-24 grid items-center gap-10 rounded-[2rem] bg-cocoa p-8 text-cream sm:p-12 lg:grid-cols-[auto_1fr]" data-id="element-396" data-mp="2di7h7w0dpipp">
          <div className="text-center lg:text-left" data-id="element-397" data-mp="2i8nspakn2u9f">
            <CountUp to={2000} data-id="element-398" data-mp="2ib0j8c4gqcy3" />
            <p className="mt-1 text-cream/70" data-id="element-399" data-mp="2n8jxvd52orqk">mujeres transformaron su relación con el dulce</p>
            <div className="mt-4 flex flex-wrap justify-center gap-2 lg:justify-start" data-id="element-400" data-mp="2s7da728micp5">
              {["Latam", "España", "USA"].map(r => <span key={r} className="flex items-center gap-1 rounded-full bg-white/10 px-3 py-1 text-sm font-semibold" data-id="element-401" data-mp="2x6px1mzhvjrq">
                  <MapPinIcon className="h-3.5 w-3.5 text-gold-light" data-id="element-402" data-mp="lgi4qyast11v" />
                  {r}
                </span>)}
            </div>
          </div>
          <p className="text-lg leading-relaxed text-cream/85 lg:border-l lg:border-white/15 lg:pl-10" data-id="element-403" data-mp="blgge4ock30c">
            Después de analizar por qué lo habían logrado, descubrí el patrón: querían postres que funcionaran en{" "}
            <b className="text-gold-light" data-id="element-404" data-mp="14bf9cb5nwkaa">SU REALIDAD</b>, no en la realidad de alguien en California.
          </p>
        </div>

        <Reveal className="mt-16" data-id="element-405" data-mp="uj9ij4r5c8wo">
          <h3 className="font-display text-2xl font-bold text-cocoa sm:text-3xl" data-id="element-406" data-mp="uihpa0l0un9k">Querían:</h3>
        </Reveal>
        <div className="mt-6 grid gap-5 sm:grid-cols-2" data-id="element-407" data-mp="pjwxmpju5bwq">
          {wants.map((w, i) => <Reveal key={w.text} delay={i * 0.05} className="h-full" data-id="element-408" data-mp="1n0lus5xubpzs" data-mpi={i}>
              <div className="flex h-full items-center gap-5 rounded-3xl bg-white p-4 shadow-[0_16px_40px_-28px_rgba(59,34,25,0.5)]" data-id="element-409" data-mp="1i8lozwv3a2va">
                <img src={w.image} alt="" className="h-24 w-24 shrink-0 rounded-2xl object-cover sm:h-28 sm:w-28" loading="lazy" data-id="element-410" data-mp="12pj2jw43jggm" />
                <p className="text-lg font-medium leading-snug text-cocoa" data-id="element-411" data-mp="17nud6927tuzw">{w.text}</p>
              </div>
            </Reveal>)}
        </div>

        <Reveal className="mt-20 text-center" data-id="element-412" data-mp="xt2d8yl5uw1y">
          <p className="text-lg text-cocoa-light" data-id="element-413" data-mp="xtu8u9ercts8">Y nació el</p>
          <p className="mt-1 font-display text-4xl font-bold text-cocoa sm:text-6xl" data-id="element-414" data-mp="1li3sb4oi15g7">
            Pack Dulce <span className="font-script text-5xl font-normal text-berry sm:text-7xl" data-id="element-415" data-mp="1qg5a7ma6uyq3">Saludable</span>
          </p>
        </Reveal>
      </div>
    </section>;
}
function CountUp({
  to
}: {
  to: number;
}) {
  const ref = useRef<HTMLParagraphElement>(null);
  const inView = useInView(ref, {
    once: true
  });
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, to, {
      duration: 1.2,
      ease: "easeOut",
      onUpdate: v => setValue(Math.round(v))
    });
    return () => controls.stop();
  }, [inView, to]);
  return <motion.p ref={ref} className="font-display text-6xl font-bold text-gold-light tabular sm:text-7xl" data-id="element-416" data-mp="3oza25ymn8rqz">
      +{value.toLocaleString("en-US")}
    </motion.p>;
}